import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, CheckCircle2, Circle, ChevronDown, ChevronUp, ExternalLink, GraduationCap, Loader2, Plus, Trash2, X } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { Button } from '../../components/ui/button'
import PullToRefresh from '../../components/PullToRefresh'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useEnglishCourses } from '../../hooks/useEnglishCourses'
import { performRefresh } from '../../lib/refresh'
import { cn } from '../../lib/utils'

// Add-course modal. Captures the bare minimum to start tracking —
// title + platform + total lectures — and the URL so we can render
// an "Open course" link on each card. Owner can edit later via the
// inline pencil (PR D+1 if needed).
function AddCourseSheet({ open, onClose, onSave, saving }) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [platform, setPlatform] = useState('udemy')
  const [url, setUrl] = useState('')
  const [instructor, setInstructor] = useState('')
  const [totalLectures, setTotalLectures] = useState('')

  useEffect(() => {
    if (!open) {
      setTitle(''); setPlatform('udemy'); setUrl(''); setInstructor(''); setTotalLectures('')
    }
  }, [open])

  if (!open) return null

  const submit = async (e) => {
    e?.preventDefault?.()
    if (!title.trim()) return
    const res = await onSave({
      title: title.trim(),
      platform: platform.trim() || 'udemy',
      url: url.trim() || null,
      instructor: instructor.trim() || null,
      total_lectures: totalLectures.trim() ? parseInt(totalLectures, 10) : 0,
    })
    if (!res?.error) onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-3 pt-3"
      style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}
      onClick={onClose}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-foreground" dir="auto">{t('english.course.addTitle')}</h2>
            <button type="button" onClick={onClose} aria-label={t('english.course.cancel')}>
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          <form onSubmit={submit} className="space-y-2">
            <Field label={t('english.course.titleLabel')} value={title} onChange={setTitle} placeholder={t('english.course.titlePlaceholder')} autoFocus />
            <Field label={t('english.course.platformLabel')} value={platform} onChange={setPlatform} placeholder="udemy" />
            <Field label={t('english.course.urlLabel')} value={url} onChange={setUrl} placeholder="https://…" />
            <Field label={t('english.course.instructorLabel')} value={instructor} onChange={setInstructor} placeholder={t('english.course.instructorPlaceholder')} />
            <Field label={t('english.course.totalLabel')} value={totalLectures} onChange={setTotalLectures} placeholder="0" inputMode="numeric" />
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-app-primary-500 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t('english.course.save')}
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

function AddLectureSheet({ open, onClose, onSave, saving, courseTitle }) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [section, setSection] = useState('')
  const [lectureNumber, setLectureNumber] = useState('')
  const [durationMinutes, setDurationMinutes] = useState('')
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (!open) {
      setTitle(''); setSection(''); setLectureNumber(''); setDurationMinutes(''); setNotes('')
    }
  }, [open])

  if (!open) return null

  const submit = async (e) => {
    e?.preventDefault?.()
    if (!title.trim()) return
    const res = await onSave({
      title: title.trim(),
      section: section.trim() || null,
      lecture_number: lectureNumber.trim() ? parseInt(lectureNumber, 10) : null,
      duration_minutes: durationMinutes.trim() ? parseInt(durationMinutes, 10) : null,
      notes: notes.trim() || null,
    })
    if (!res?.error) onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-3 pt-3"
      style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}
      onClick={onClose}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground truncate" dir="auto">{t('english.course.lectureAddTitle')}</h2>
              {courseTitle && <p className="text-[11px] text-muted-foreground truncate" dir="auto">{courseTitle}</p>}
            </div>
            <button type="button" onClick={onClose} aria-label={t('english.course.cancel')}>
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          <form onSubmit={submit} className="space-y-2">
            <Field label={t('english.course.lectureTitleLabel')} value={title} onChange={setTitle} placeholder={t('english.course.lectureTitlePlaceholder')} autoFocus />
            <div className="grid grid-cols-2 gap-2">
              <Field label={t('english.course.lectureNumberLabel')} value={lectureNumber} onChange={setLectureNumber} placeholder="1" inputMode="numeric" />
              <Field label={t('english.course.durationLabel')} value={durationMinutes} onChange={setDurationMinutes} placeholder={t('english.course.durationPlaceholder')} inputMode="numeric" />
            </div>
            <Field label={t('english.course.sectionLabel')} value={section} onChange={setSection} placeholder={t('english.course.sectionPlaceholder')} />
            <div>
              <label className="text-[11px] text-muted-foreground" dir="auto">{t('english.course.notesLabel')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('english.course.notesPlaceholder')}
                rows={2}
                className="w-full px-3 py-2 mt-1 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500 resize-none"
                dir="auto"
              />
            </div>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-app-primary-500 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t('english.course.lectureSave')}
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

function Field({ label, value, onChange, placeholder, autoFocus, inputMode }) {
  return (
    <div>
      <label className="text-[11px] text-muted-foreground" dir="auto">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        inputMode={inputMode}
        className="w-full px-3 py-2 mt-1 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
        dir="auto"
      />
    </div>
  )
}

function CourseCard({ course, lectures, onAddLecture, onToggleLecture, onRemoveLecture, onRemoveCourse }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const completedNow = course.completed_lectures || 0
  const total = course.total_lectures || lectures.length
  const pct = total > 0 ? Math.min(100, Math.round((completedNow / total) * 100)) : 0
  // Group lectures by section. Falls back to a single bucket when
  // the course has no sections set.
  const grouped = useMemo(() => {
    const map = new Map()
    for (const l of lectures) {
      const key = l.section || ''
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(l)
    }
    return Array.from(map.entries())
  }, [lectures])

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-3 sm:p-4 space-y-2">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-foreground leading-tight" dir="auto">{course.title}</h3>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5" dir="auto">
              {course.platform && <span className="uppercase tracking-wide">{course.platform}</span>}
              {course.instructor && <><span>·</span><span>{course.instructor}</span></>}
            </div>
          </div>
          {course.url && (
            <a
              href={course.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('english.course.openSource')}
              className="p-1.5 rounded-md text-muted-foreground hover:text-app-primary-500 hover:bg-accent"
            >
              <ExternalLink className="w-4 h-4 rtl:-scale-x-100" />
            </a>
          )}
          {confirmingDelete ? (
            <button
              type="button"
              onClick={() => { setConfirmingDelete(false); onRemoveCourse(course.id) }}
              aria-label={t('english.course.confirmDelete')}
              className="p-1.5 rounded-md text-red-500 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              aria-label={t('english.course.delete')}
              className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-accent"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground" dir="auto">
            <span>{t('english.course.progress', { done: completedNow, total })}</span>
            <span className="font-mono" dir="ltr">{pct}%</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-app-primary-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setExpanded((v) => !v)}
            className="flex-1"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5 me-1" /> : <ChevronDown className="w-3.5 h-3.5 me-1" />}
            {expanded ? t('english.course.hideLectures') : t('english.course.showLectures', { count: lectures.length })}
          </Button>
          <Button
            size="sm"
            onClick={() => onAddLecture(course)}
            className="flex-shrink-0"
          >
            <Plus className="w-3.5 h-3.5 me-1" />
            {t('english.course.addLecture')}
          </Button>
        </div>

        {expanded && (
          <div className="pt-2 space-y-2 border-t border-border">
            {grouped.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2" dir="auto">{t('english.course.lectureEmpty')}</p>
            ) : grouped.map(([section, items]) => (
              <div key={section || '__no_section__'} className="space-y-1">
                {section && (
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold" dir="auto">
                    {section}
                  </p>
                )}
                <ul className="space-y-0.5">
                  {items.map((l) => (
                    <li key={l.id} className="flex items-center gap-2 py-1">
                      <button
                        type="button"
                        onClick={() => onToggleLecture(l.id)}
                        aria-label={l.completed_at ? t('english.course.markIncomplete') : t('english.course.markComplete')}
                        className={cn(
                          'flex-shrink-0 p-0.5',
                          l.completed_at ? 'text-emerald-500' : 'text-muted-foreground hover:text-app-primary-500',
                        )}
                      >
                        {l.completed_at ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-sm text-foreground truncate', l.completed_at && 'line-through opacity-60')} dir="auto">
                          {l.lecture_number != null && <span className="text-muted-foreground font-mono me-1" dir="ltr">{l.lecture_number}.</span>}
                          {l.title}
                        </p>
                        {l.notes && (
                          <p className="text-[11px] text-muted-foreground line-clamp-1" dir="auto">{l.notes}</p>
                        )}
                      </div>
                      {l.duration_minutes != null && (
                        <span className="text-[10px] text-muted-foreground font-mono" dir="ltr">{l.duration_minutes}m</span>
                      )}
                      <button
                        type="button"
                        onClick={() => onRemoveLecture(l.id)}
                        aria-label={t('english.course.delete')}
                        className="p-1 text-muted-foreground hover:text-red-500"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function EnglishCoursePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  useEffect(() => {
    if (authLoading) return
    if (!user || !hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, authLoading, navigate])

  const hook = useEnglishCourses()
  const [addCourseOpen, setAddCourseOpen] = useState(false)
  const [savingCourse, setSavingCourse] = useState(false)
  const [addLectureFor, setAddLectureFor] = useState(null) // course row
  const [savingLecture, setSavingLecture] = useState(false)

  const handleAddCourse = async (payload) => {
    setSavingCourse(true)
    try { return await hook.addCourse(payload) } finally { setSavingCourse(false) }
  }
  const handleAddLecture = async (payload) => {
    if (!addLectureFor) return { error: 'no course' }
    setSavingLecture(true)
    try {
      return await hook.addLecture({ course_id: addLectureFor.id, ...payload })
    } finally {
      setSavingLecture(false)
    }
  }

  const goBack = () => navigate('/english')
  const handleRefresh = () => performRefresh([hook.refetch])

  const lecturesByCourse = useMemo(() => {
    const map = new Map()
    for (const l of hook.lectures) {
      if (!map.has(l.course_id)) map.set(l.course_id, [])
      map.get(l.course_id).push(l)
    }
    return map
  }, [hook.lectures])

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-foreground flex items-center gap-1.5" dir="auto">
              <GraduationCap className="w-4 h-4 text-app-primary-500" />
              {t('english.course.title')}
            </h1>
            <p className="text-xs text-muted-foreground" dir="auto">
              {t('english.course.subtitle', { count: hook.courses.length })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAddCourseOpen(true)}
            aria-label={t('english.course.addTitle')}
            className="p-1.5 rounded-md text-app-primary-500 hover:bg-accent"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {hook.loading ? (
          <div className="space-y-2">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : hook.courses.length === 0 ? (
          <EmptyState
            icon={GraduationCap}
            title={t('english.course.empty')}
            description={t('english.course.emptyHint')}
          />
        ) : (
          <div className="space-y-2">
            {hook.courses.map((c) => (
              <CourseCard
                key={c.id}
                course={c}
                lectures={lecturesByCourse.get(c.id) || []}
                onAddLecture={(course) => setAddLectureFor(course)}
                onToggleLecture={hook.toggleLectureComplete}
                onRemoveLecture={hook.removeLecture}
                onRemoveCourse={hook.removeCourse}
              />
            ))}
          </div>
        )}

        <AddCourseSheet
          open={addCourseOpen}
          onClose={() => setAddCourseOpen(false)}
          onSave={handleAddCourse}
          saving={savingCourse}
        />
        <AddLectureSheet
          open={!!addLectureFor}
          onClose={() => setAddLectureFor(null)}
          onSave={handleAddLecture}
          saving={savingLecture}
          courseTitle={addLectureFor?.title}
        />
      </div>
    </PullToRefresh>
  )
}
